import { Badge, Loader, Text, ThemeIcon } from "@mantine/core";
import { Payment } from "../../utils/interfaces";
import { IntentionStatus } from "../../utils/types";
import { intentionStatusData } from "../../utils/consts";
import { useLastBucketStatusQuery } from "../../api/queries/paymentsQueries";

export function shouldFetchCustomerInvoiceBucketStatus(
  paymentStatus: string,
): boolean {
  return paymentStatus.toLowerCase() !== "paid";
}

type BucketStatusCellVariant = "status" | "authorBadge" | "both";

export const BucketStatusCell = ({
  payment,
  variant = "both",
}: {
  payment: Payment;
  variant?: BucketStatusCellVariant;
}) => {
  const { data: lastBucketStatus, isLoading } = useLastBucketStatusQuery(
    payment.external_id,
    {
      enabled:
        !!payment.external_id &&
        shouldFetchCustomerInvoiceBucketStatus(payment.payment_status),
    },
  );
  const showStatus = variant === "status" || variant === "both";
  const showAuthorBadge = variant === "authorBadge" || variant === "both";

  if (isLoading) {
    return <Loader size="xs" />;
  }

  if (lastBucketStatus) {
    const statusText = lastBucketStatus.current_status as IntentionStatus;
    const description = lastBucketStatus.description;
    const statusData = intentionStatusData[statusText];
    const Icon = statusData?.icon;

    if (variant === "authorBadge") {
      return (
        <Badge>
          {lastBucketStatus.is_llm
            ? "LLM"
            : lastBucketStatus.author_name
              ? lastBucketStatus.author_name
              : "-"}
        </Badge>
      );
    }

    if (Icon) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          {showStatus ? (
            <div className="flex items-center gap-2">
              <ThemeIcon
                color={statusData.tableIconColor}
                variant="transparent"
                size="md"
              >
                <Icon size={18} />
              </ThemeIcon>
              {description && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{
                    maxWidth: "200px",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  {description}
                </Text>
              )}
            </div>
          ) : null}
          {showAuthorBadge ? (
            <Badge>
              {lastBucketStatus.is_llm
                ? "LLM"
                : lastBucketStatus.author_name
                  ? lastBucketStatus.author_name
                  : "-"}
            </Badge>
          ) : null}
        </div>
      );
    }
  }

  const fallbackStatus = payment.intention_status as IntentionStatus;
  const fallbackStatusData = intentionStatusData[fallbackStatus];
  const FallbackIcon = fallbackStatusData?.icon;

  if (variant === "authorBadge") {
    return (
      <Badge size="sm" variant="light">
        -
      </Badge>
    );
  }

  if (FallbackIcon) {
    return (
      <ThemeIcon
        color={fallbackStatusData.tableIconColor}
        variant="transparent"
        size="md"
      >
        <FallbackIcon size={18} />
      </ThemeIcon>
    );
  }

  return (
    <Badge size="sm" variant="light">
      {payment.intention_status}
    </Badge>
  );
};
